// BrandList.js
import React, { useEffect, useState } from "react";
import {
  FaEdit,
  FaEye,
  FaPlus,
  FaToggleOn,
  FaToggleOff,
  FaArrowLeft,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCampaigns,
  updateCampaignStatusThunk,
} from "../../../Redux/campaignSlice";
import Swal from "sweetalert2";
import { campaignService } from "../../services/companyAuthService";

const CampaignList = () => {
  const dispatch = useDispatch();
  const { campaigns, loading, error } = useSelector((state) => state.campaign);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const navigate = useNavigate();
  const [campaignLimits, setCampaignLimits] = useState(null);
  const [limitsLoading, setLimitsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchCampaigns()).unwrap();
      } catch (err) {
        console.error("Error fetching campaigns:", err);
        toast.error(err || "Failed to fetch campaigns");
      }
    };
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    const fetchLimits = async () => {
      try {
        setLimitsLoading(true);
        const res = await campaignService.getCampaignLimits();
        if (res?.success && res?.data) setCampaignLimits(res.data);
      } catch {
        setCampaignLimits(null);
      } finally {
        setLimitsLoading(false);
      }
    };
    fetchLimits();
  }, []);

  // Ensure campaigns is always an array
  const safeCampaigns = Array.isArray(campaigns) ? campaigns : [];

  // Sort campaigns - active first by date, then deactivated by date
  const sortedCampaigns = [...safeCampaigns].sort((a, b) => {
    // First sort by status (active first)
    if (a.status === "active" && b.status !== "active") return -1;
    if (a.status !== "active" && b.status === "active") return 1;
    
    // If both are active or both are inactive, sort by date
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    
    // For active campaigns, latest first
    if (a.status === "active" && b.status === "active") {
      return dateB - dateA;
    }
    
    // For inactive campaigns, latest first
    if (a.status !== "active" && b.status !== "active") {
      return dateB - dateA;
    }
    
    return 0;
  });

  // Get current page data
  const indexOfLastCampaign = currentPage * itemsPerPage;
  const indexOfFirstCampaign = indexOfLastCampaign - itemsPerPage;
  const currentCampaigns = sortedCampaigns.slice(
    indexOfFirstCampaign,
    indexOfLastCampaign
  );
  const totalPages = Math.ceil(sortedCampaigns.length / itemsPerPage);

  // Handle page navigation
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Handle View Campaign
  const handleView = (campaign) => {
    navigate(`/company/dashboard/campaigns/view/${campaign._id}`);
  };

  // Handle Edit Campaign
  const handleEdit = (campaign) => {
    if (campaign.isDeactivated) {
      toast.error("Cannot edit a deactivated campaign");
      return;
    }
    navigate(`/company/dashboard/campaigns/edit/${campaign._id}`);
  };

  // Handle Toggle Active
  const handleStatusToggle = async (campaign) => {
    if (campaign.status === "active") {
      const result = await Swal.fire({
        title: "Warning!",
        html: `
          <p>Are you sure you want to deactivate this campaign?</p>
          <p class="text-red-500 font-medium mt-2">This action is permanent and cannot be undone.</p>
        `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#6b7280",
        confirmButtonText: "Yes, deactivate",
        cancelButtonText: "Cancel",
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    try {
      await dispatch(
        updateCampaignStatusThunk({
          id: campaign._id,
          status: campaign.status === "active" ? "inactive" : "active",
          isDeactivated: campaign.status === "active",
        })
      ).unwrap();

      toast.success(
        campaign.status === "active"
          ? "Campaign deactivated permanently"
          : "Campaign activated successfully"
      );
    } catch (error) {
      toast.error(error.message || "Failed to update status");
    }
  };

  if (loading) {
    return (
      <div className="h-[calc(100dvh-80px)] bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
        <p className="text-gray-600 text-xs sm:text-sm md:text-lg">Loading...</p>
      </div>
    </div>
    );
  }

  const canCreate = campaignLimits?.canCreate !== false;
  const limitLabel =
    campaignLimits?.campaignLimit != null
      ? campaignLimits.campaignLimitType === "per_month"
        ? `${campaignLimits.campaignsUsed} / ${campaignLimits.campaignLimit} this month`
        : `${campaignLimits.campaignsUsed} / ${campaignLimits.campaignLimit} active`
      : campaignLimits?.campaignsUsed != null
        ? `${campaignLimits.campaignsUsed} campaigns`
        : null;

  return (
    <>
      <ToastContainer />
      <div className="container mx-auto px-2 md:px-4 py-4 md:py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate("/company/dashboard/")}
            className="inline-flex items-center px-2 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-colors duration-200"
          >
            <FaArrowLeft className="mr-2" /> Back
          </button>
        </div>
        <div className="flex flex-wrap justify-between items-center mb-6 gap-1 md:gap-2 lg:mb-10">
          <h1 className="text-base md:text-xl font-bold text-gray-900 mb-0 whitespace-nowrap">Campaigns</h1>
          <div className="flex items-center gap-2 md:gap-3">
            {!limitsLoading && limitLabel && (
              <span className="text-xs md:text-sm text-gray-500">{limitLabel}</span>
            )}
            {canCreate ? (
              <Link
                to="/company/dashboard/campaigns/create"
                className="bg-orange-500 text-white px-2.5 md:px-4 py-1.5 rounded-lg inline-flex items-center justify-center gap-1 hover:bg-orange-600 transition-colors duration-200 whitespace-nowrap flex-shrink-0 leading-none"
              >
                <FaPlus className="text-xs md:text-sm" />
                <span className="text-xs md:text-sm font-medium">Create Campaign</span>
              </Link>
            ) : (
              <span
                className="bg-gray-300 text-gray-500 px-2.5 md:px-4 py-1.5 rounded-lg inline-flex items-center justify-center gap-1 hover:bg-gray-400 transition-colors duration-200 whitespace-nowrap flex-shrink-0 leading-none cursor-not-allowed"
                title="Campaign limit reached. Upgrade your plan to create more campaigns."
              >
                <FaPlus className="text-xs md:text-sm" />
                <span className="text-xs md:text-sm font-medium">Create Campaign</span>
              </span>
            )}
          </div>
        </div>

        {!canCreate && !limitsLoading && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-2 md:px-4 py-2 md:py-3 rounded-lg mb-4">
            You have reached your campaign limit for your current plan. Deactivate a campaign or upgrade your plan to create more.
          </div>
        )}

        {error ? (
          <div className="bg-red-100 text-red-600 p-2 md:p-4 rounded-lg mb-4">
            {error}
          </div>
        ) : safeCampaigns.length === 0 ? (
          <div className="text-center py-4 md:py-8">
            <p className="text-gray-600 mb-4">No campaigns found</p>
            {canCreate ? (
              <Link
                to="/company/dashboard/campaigns/create"
                className="bg-orange-500 text-white px-2 md:px-4 py-1 md:py-2 rounded-lg hover:bg-orange-600"
              >
                Create your first campaign
              </Link>
            ) : (
              <p className="text-gray-500 text-xs md:text-sm">Upgrade your plan to create campaigns.</p>
            )}
          </div>
        ) : (
          <div className="bg-white text-xs md:text-sm rounded-lg shadow p-2 md:p-4 overflow-x-auto">
            <table className="min-w-full table-auto divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border p-1 md:p-2 text-left">Sr. No</th>
                  <th className="border p-1 md:p-2 text-left">Name</th>
                  <th className="border p-1 md:p-2 text-left">Brand</th>
                  <th className="border p-1 md:p-2 text-left">Campaign Type</th>
                  <th className="border p-1 md:p-2 text-left">Commission Model</th>
                  <th className="border p-1 md:p-2 text-center">Actions</th>
                  <th className="border p-1 md:p-2 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentCampaigns.map((item, index) => (
                  <tr
                    key={item._id}
                    className={`hover:bg-gray-50 ${
                      item.status !== "active" ? "opacity-50" : ""
                    }`}
                  >
                    <td className="border p-1 md:p-2">
                      {indexOfFirstCampaign + index + 1}
                    </td>
                    <td className="border p-1 md:p-2">{item.name}</td>
                    <td className="border p-1 md:p-2">{item.brandName}</td>
                    <td className="border p-1 md:p-2">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                        item.campaignType === 'Advertising' 
                            ? 'bg-blue-100 text-blue-800' 
                            : item.campaignType === 'Affiliate'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-orange-100 text-orange-800'
                      }`}>
                        {item.campaignType}
                      </span>
                    </td>
                    <td className="border p-1 md:p-2">
                      {item.commission?.model === "fixed" 
                        ? `Fixed (${item.commission?.currency?.symbol}${item.commission?.amount})`
                        : `Percentage (${item.commission?.percentage}%)`}
                      {item.campaignType === "Advertising" && (
                        <div className="text-xs text-blue-600 mt-1">
                          Fixed only
                        </div>
                      )}
                    </td>
                    <td className="border p-1 md:p-2 flex justify-center space-x-2">
                      <button
                        onClick={() => handleView(item)}
                        className="bg-blue-100 text-blue-700 p-1 md:p-2 rounded hover:bg-blue-200"
                      >
                        <FaEye />
                      </button>
                      <button
                        onClick={() => handleEdit(item)}
                        className={`bg-green-100 text-green-700 p-1 md:p-2 rounded hover:bg-green-200 ${
                          item.status !== "active" ? "hidden" : ""
                        }`}
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleStatusToggle(item)}
                        className={`bg-yellow-100 text-yellow-700 p-1 md:p-2 rounded hover:bg-yellow-200 ${
                          item.status !== "active" ? "hidden" : ""
                        }`}
                      >
                        {item.status === "active" ? (
                          <FaToggleOn />
                        ) : (
                          <FaToggleOff />
                        )}
                      </button>
                    </td>
                    <td className="border p-1 md:p-2">
                      {item.status === "active" ? (
                        <span className="text-green-500">Active</span>
                      ) : (
                        <span className="text-red-500">Inactive</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-2 md:mt-4">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className={`px-2 md:px-4 py-1 md:py-2 rounded ${
                currentPage === 1
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-orange-500 text-white hover:bg-orange-600"
              }`}
            >
              Previous
            </button>
            
            <div className="flex items-center space-x-1 md:space-x-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-2 md:px-3 py-1 md:py-2 rounded ${
                    currentPage === page
                      ? "bg-orange-500 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`px-2 md:px-4 py-1 md:py-2 rounded ${
                currentPage === totalPages
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-orange-500 text-white hover:bg-orange-600"
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
};

// Modal Component
export const CampaignEditModal = ({ campaign, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: campaign.name,
    description: campaign.description,
    commission: {
      model: campaign.commission.model,
      amount: campaign.commission.amount,
      percentage: campaign.commission.percentage,
      currency: campaign.commission.currency,
    },
    landingPage: campaign.landingPage,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCommissionChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      commission: {
        ...prev.commission,
        [name]: value,
      },
    }));
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
      <div className="bg-white p-2 md:p-6 rounded-lg w-96">
        <h2 className="text-2xl font-semibold mb-4 text-center">
          Edit Campaign
        </h2>
        <form className="space-y-4">
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Campaign Name"
            className="w-full p-1 md:p-2 border rounded"
          />
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Description"
            className="w-full p-1 md:p-2 border rounded"
          />
          <input
            type="text"
            name="brand"
            value={campaign.brandId?.brandName}
            onChange={handleChange}
            placeholder="Brand"
            className="w-full p-1 md:p-2 border rounded"
          />
          <select
            name="currency"
            value={formData.commission.currency.symbol}
            onChange={handleChange}
            className="w-full p-1 md:p-2 border rounded"
          >
            <option value="USD">$</option>
            <option value="EUR">€</option>
            <option value="GBP">£</option>
            <option value="CAD">$</option>
            <option value="AUD">$</option>
          </select>
          <select
            name="commissionModel"
            value={formData.commission.model}
            onChange={handleChange}
            className="w-full p-1 md:p-2 border rounded"
          >
            <option value="Fixed">Fixed</option>
            <option value="Percentage">Percentage</option>
          </select>
          {formData.commission.model === "Fixed" && (
            <input
              type="text"
              name="amount"
              value={formData.commission.amount}
              onChange={handleCommissionChange}
              placeholder="Payout Amount"
              className="w-full p-1 md:p-2 border rounded"
            />
          )}
          {formData.commission.model === "Percentage" && (
            <input
              type="text"
              name="percentage"
              value={formData.commission.percentage}
              onChange={handleCommissionChange}
              placeholder="Percentage"
              className="w-full p-1 md:p-2 border rounded"
            />
          )}
          <input
            type="url"
            name="landingPage"
            value={formData.landingPage}
            onChange={handleChange}
            placeholder="Landing Page URL"
            className="w-full p-1 md:p-2 border rounded"
          />
        </form>
        <div className="flex justify-end space-x-1 md:space-x-2 mt-2 md:mt-4">
          <button
            onClick={onClose}
            className="bg-gray-400 text-white px-2 md:px-4 py-1 md:py-2 rounded hover:bg-gray-500"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(formData)}
            className="bg-green-500 text-white px-2 md:px-4 py-1 md:py-2 rounded hover:bg-green-600"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignList;
